import { neutralizeCsvRow } from "@/lib/csvSafe";

import type { MetaCampaignReportRow } from "../../types";
import { canAccess, type Actor } from "./canAccess";

/** The message the report export raises for a role outside the export gate. */
export const META_REPORT_EXPORT_DENIED_MESSAGE =
  "Tu rol no puede exportar este reporte";

/**
 * Almikia Epic EM (TASK-067): the CSV rows of the Meta campaign report. Column keys are the
 * i18n header keys under `crm.metaOrigin.report.headers` (the screen translates them when it
 * hands the rows to the CSV writer). Rates become percentages with two decimals and the median
 * first response becomes minutes. Every free-text cell (campaign / ad set / ad names come from
 * Meta) goes through `neutralizeCsvRow`, so a name starting with `=` is exported as text.
 * Refused for any role outside the export gate (direccion / lead_manager).
 */
export const buildMetaCampaignReportCsvRows = (
  actor: Actor,
  rows: MetaCampaignReportRow[],
): Record<string, unknown>[] => {
  if (
    !canAccess(actor, { action: "export", resource: "meta_campaign_report" })
  ) {
    throw new Error(META_REPORT_EXPORT_DENIED_MESSAGE);
  }
  const percent = (rate: number | null) =>
    rate == null ? null : Math.round(rate * 10000) / 100;
  return rows.map((row) =>
    neutralizeCsvRow({
      campaign: row.campaign_name ?? row.campaign_id,
      adset: row.adset_name ?? row.adset_id,
      ad: row.ad_name ?? row.ad_id,
      platform: row.platform,
      leads: row.leads,
      qualified_leads: row.qualified_leads,
      appointments: row.appointments,
      reservations: row.reservations,
      sales: row.sales,
      sale_value: row.sale_value,
      qualified_rate: percent(row.qualified_rate),
      appointment_rate: percent(row.appointment_rate),
      sales_rate: percent(row.sales_rate),
      median_first_response:
        row.median_first_response_seconds == null
          ? null
          : Math.round(row.median_first_response_seconds / 60),
    }),
  );
};
